import { NotificationType } from "evangelist-common/enums";
import type { RouterOutput } from "~/plugins/trpc.client";
import type { NotificationDto } from "~~/_api_generated_";

interface State {
  user: RouterOutput["getCurrentUser"];
  loginLoading: boolean;
  notifications: NotificationDto[];
  notificationSidebarOpen: boolean;
  openedCampaignWeekId: string | null;
  openedCampaignUuid: string | null;
}
const defaultUserState = {
  userId: null,
  realUserId: null,
  profileImageUrl: null,
  twitterHandle: false,
  userName: false,
  userRoles: [],
  twoFactorAuthEnabled: false,
  verifiedTwoFactorAuthEnabled: false,
  email: null,
  verifiedEmail: null,
};

const allowedEventTypes = [
  NotificationType.Welcome,
  NotificationType.CampaignJoined,
  NotificationType.CampaignEnded,
  NotificationType.CampaignEligible,
  NotificationType.CampaignNotEligible,
  NotificationType.CampaignMaturity,
  NotificationType.NewEligiblePosts,
  // NotificationType.NewRewardedPosts,
  NotificationType.TransactionVerifying,
  NotificationType.TransactionCompleted,
  NotificationType.TermsAndConditions,
  NotificationType.AdHoc,
  NotificationType.VlayerBadge,
];

export const useDefaultStore = defineStore("default", {
  state: (): State => ({
    // @ts-expect-error this is expected error because the default state is parially nullable
    user: {
      ...defaultUserState,
    },
    loginLoading: false,
    notifications: [],
    notificationSidebarOpen: false,
    openedCampaignWeekId: null,
    openedCampaignUuid: null,
  }),
  actions: {
    openNotificationSidebar() {
      this.notificationSidebarOpen = true;
    },

    closeNotificationSidebar() {
      this.notificationSidebarOpen = false;
    },

    loadUser() {
      const { $trpc } = useNuxtApp();

      const userPromise = $trpc.getCurrentUser.query();

      userPromise.then((user) => {
        this.user = user;
        trackUser(user.userId);

        setUserPropertiesOnce({
          first_referrer: document.referrer,
          first_advertiser: user.preferredAdvertiserName?.toLowerCase(),
        });

        setUserProperties({
          referrer: document.referrer,
          advertiser: user.preferredAdvertiserName?.toLowerCase(),
          x_handle: user.twitterHandle,
          x_user_name: user.userName,
        });
      });

      return userPromise;
    },

    refreshNotifications() {
      const response = apiService.refreshNotifications();

      response
        .then((res) => {
          let notifications = res.data;
          notifications = notifications.filter((i) =>
            allowedEventTypes.includes(i.eventType as NotificationType),
          );

          this.notifications = notifications;
        })
        .catch(() => {
          errorMessage("Error! Can not get notifications.");
        });

      return response;
    },

    clearUser() {
      // @ts-expect-error this is expected error because the default state is parially nullable
      this.user = defaultUserState;
    },

    campaignPageOpened({
      campaignUuid,
      campaignWeekId,
    }: {
      campaignUuid: string | null;
      campaignWeekId: string | null;
    }) {
      this.openedCampaignUuid = campaignUuid;
      this.openedCampaignWeekId = campaignWeekId;
    },
  },

  getters: {
    computedNotifications(state): NotificationDto[] {
      return state.notifications.filter(
        (i) => !["welcome"].includes(i.eventType),
      );
    },

    notificationCount(): number {
      return (this.computedNotifications ?? []).filter((i) => !i.isRead).length;
    },

    ftuNotification(state): NotificationDto | undefined {
      return (state.notifications ?? []).find(
        (i) => !i.isRead && i.eventType == "welcome",
      );
    },

    campaignEligibleNotifications(state): NotificationDto[] {
      return (state.notifications ?? []).filter(
        (i) => !i.isRead && i.eventType == "campaign-eligible",
      );
    },
  },
});
